import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  EngagementKeys,
  getQueryAdderColumnsByEventHandlerType,
  getQueryAdderKeysByEventHandlerType,
  IssueAndEngagementSharedValues,
  IssueAndServiceSharedValues,
  IssueKeys,
  queryAdderEngagementFields,
  queryAdderMediaFields,
  queryAdderServiceKeys,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';
import { QueryAdderCell } from './QueryAdderCell';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, Button, Divider, LinearProgress, Stack, Typography, useTheme } from '@mui/material';
import dayjs from '../../utils/dayjs';
import { getTabColorHeaderMain, TrackerDataType } from '../../shared/constants/constants';
import { rowOptions } from '../../shared/constants/commonRowValues';
import ErrorBoundary from '../ErrorBoundary';
import { getServiceById } from '../../shared/requests/serviceNetworkRequests';
import { processFromDBForAutoComplete } from '../../shared/functions/autoCompleteProcessing';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { sendNotification } from '../../shared/requests/notifications';
import findOrganizationFromContact from '../../shared/functions/findOrganizationFromContact';
import VerificationModal from '../VerificationModal/VerificationModal';
import { LoadingButton } from '@mui/lab';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px 5px',
    alignItems: 'center',
    overflowY: 'scroll',
    justifyContent: 'space-between',
    '& > label': {
      width: '100%',
    },
    '& > div': {
      flex: '1 1 220',
    },
  },
  containerDrag: {
    border: 'dashed grey 4px',
    display: 'flex',
    backgroundColor: '#cbd5e1',
    flexWrap: 'wrap',
    gap: '10px 5px',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > label': {
      width: '100%',
    },
    '& > div': {
      flex: '1 1 220',
    },
  },
  adderButton: {
    width: '220px',
    height: '40px',
    fontSize: '16px',
    margin: '0 10px',
    fontWeight: 550,
    // '&.MuiButton-contained': {
    //   background: ({ tabColor }) => tabColor,
    //   color: 'white',
    // },
  },
  cancelButton: {
    width: '220px',
    height: '40px',
    fontSize: '16px',
    margin: '0',
    fontWeight: 550,
    '&.MuiButton-contained': {
      background: ({ tabColor }) => tabColor,
      color: 'white',
    },
  },
  textInput: {
    width: '100%',
  },
  title: {
    fontWeight: 500,
    fontSize: '24px',
  },
}));

const textInput = ['topic', 'response', 'service', 'keyMessaging', 'actions'];

export default function QueryAdder(props) {
  const {
    title,
    dropdownTable,
    setOpenForm,
    eventHandler,
    cells,
    issueId,
    linkedType,
    forAutoTracker,
    initialFiles,
    emailScrapeCallback,
    email,
    isUpdatingRow = false,
    onDelete,
  } = props;

  const { pathname } = useLocation();

  const theme = useTheme();
  const classes = useStyles({ tabColor: getTabColorHeaderMain(pathname, theme) });
  const columns = useMemo(() => getQueryAdderColumnsByEventHandlerType(eventHandler.type), [eventHandler.type]);

  const currentUser = useSelector((state) => state.user.userInfo);

  const [isDragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState(initialFiles ?? []);
  const [deletingFiles, setDeletingFiles] = useState([]);
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);

  const resolveEngagementOrganizationsInitialState = (value, organizationOptions) => {
    if (!value) {
      return [];
    }

    const list = Array.isArray(value) ? value : [value];

    return list
      .map((item) => {
        if (item?._id) {
          return item;
        }
        if (typeof item === 'string') {
          return organizationOptions.find((organization) => organization._id === item) ?? null;
        }
        return null;
      })
      .filter(Boolean);
  };

  const resolveEngagementCommunityMembersInitialState = (value, communityMemberOptions) => {
    if (!value) {
      return [];
    }

    const list = Array.isArray(value) ? value : [value];

    return list
      .map((item) => {
        if (item?._id) {
          return item;
        }
        if (typeof item === 'string') {
          return communityMemberOptions.find((cm) => cm._id === item) ?? null;
        }
        return null;
      })
      .filter(Boolean);
  };

  const defaultDate = new Date();
  let initialState = { entryDate: defaultDate, status: rowOptions.PROGRESS };
  if (isUpdatingRow) {
    initialState = {
      entryDate: new Date(cells[IssueAndServiceSharedValues.entryDate]),
      dueDate: cells[IssueAndServiceSharedValues.dueDate] ? new Date(cells[IssueAndServiceSharedValues.dueDate]) : null,
      status: cells[IssueAndServiceSharedValues.status],
    };

    if (eventHandler.type === TrackerDataType.SERVICE) {
      initialState = {
        ...initialState,
        service: cells[ServiceKeys.service],
        complexity: cells[ServiceKeys.complexity],
        keyMessaging: cells[ServiceKeys.keyMessaging],
        actions: cells[ServiceKeys.actions],
      };
    } else if (eventHandler.type === TrackerDataType.MEDIA_INTERACTION) {
      initialState = {
        ...initialState,
        topic: cells[IssueKeys.topic],
        keyMessaging: cells[IssueKeys.keyMessaging],
        response: cells[IssueKeys.response],
        lead: cells[IssueAndServiceSharedValues.teamMembers],
      };
    } else if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
      const organizations = resolveEngagementOrganizationsInitialState(
        cells[EngagementKeys.organization],
        dropdownTable.organization || []
      );
      const communityMembers = resolveEngagementCommunityMembersInitialState(
        cells[EngagementKeys.communityMember],
        dropdownTable.communityMember || []
      );

      initialState = {
        ...initialState,
        topic: cells[EngagementKeys.topic],
        organization: organizations,
        communityMember: communityMembers,
        background: cells[EngagementKeys.background],
        notes: cells[EngagementKeys.notes],
        lead: cells[EngagementKeys.lead],
      };
    }
  }

  if (eventHandler.type === TrackerDataType.ENGAGEMENT) {
    initialState.organization = Array.isArray(initialState.organization) ? initialState.organization : [];
    initialState.communityMember = Array.isArray(initialState.communityMember) ? initialState.communityMember : [];
  }

  if (
    !isUpdatingRow &&
    (eventHandler.type === TrackerDataType.MEDIA_INTERACTION || eventHandler.type === TrackerDataType.ENGAGEMENT)
  ) {
    initialState.status = rowOptions.PROGRESS;
  }

  //importObject is an object with the following possible fields to autocomplete:
  //must prepare your object for use before using in autoComplete.
  // {
  //   Date: String representing date String,
  //   Topic: String with topic text,
  //   Service: String with service topic text,
  //   Linked Service: obj with {_id: String, name: String},
  //   Team Members: Array of Obj {_id: String, name: String},
  //   Team Member: Array of Obj {_id: String, name: String},
  //   Unit: Array of String,
  //   Campaign: Array of String,
  //   Type: Array of String,
  //   Status: String,
  //   Key Messaging: String,
  // }

  const autoPopulate = useCallback(
    (importObject) => {
      const autoPopulateState = {};
      if (eventHandler.type === TrackerDataType.SERVICE && linkedType === TrackerDataType.MEDIA_INTERACTION) {
        autoPopulateState.linkedInteraction = [issueId];
      }
      if (eventHandler.type === TrackerDataType.SERVICE && linkedType === TrackerDataType.ENGAGEMENT) {
        autoPopulateState.linkedEngagement = [issueId];
      }
      const keys = getQueryAdderKeysByEventHandlerType(eventHandler.type);

      columns.forEach((column) => {
        let autoPopObj = null;
        switch (column) {
          case 'date':
            break;
          case 'entryDate':
            break;
          case 'dueDate':
            break;
          case 'topic':
            autoPopObj = importObject[ServiceKeys.service];
            break;
          case 'service':
            autoPopObj = importObject[IssueKeys.topic];
            break;
          case 'linkedService': {
            let obj = {
              _id: importObject._id,
              name: importObject[ServiceKeys.service],
            };
            autoPopObj = [obj];

            if (
              isUpdatingRow &&
              (eventHandler.type === TrackerDataType.MEDIA_INTERACTION ||
                eventHandler.type === TrackerDataType.ENGAGEMENT)
            ) {
              autoPopObj = importObject[IssueAndEngagementSharedValues.linkedService];
            }
            break;
          }
          case 'lead':
            // importObject[ServiceKeys.teamMember]
            //   ? (autoPopObj = [importObject[ServiceKeys.teamMember]])
            //   : (autoPopObj = null);
            autoPopObj = null;
            break;
          case 'expert':
            if (importObject[ServiceKeys.unitLead]?.[0]?.name) {
              autoPopObj = importObject[ServiceKeys.unitLead];
            } else {
              autoPopObj = [];
              importObject[ServiceKeys.unitLead]?.forEach((unitLeadId) => {
                const expertObject = dropdownTable['expert'].find((member) => member._id === unitLeadId);
                if (expertObject) {
                  autoPopObj.push(expertObject);
                }
              });
            }
            break;
          case 'keyMessaging':
          case 'response':
            break;
          case 'unitLead':
            autoPopObj = importObject[IssueKeys.expert];
            break;
          case 'teamMember':
            if (isUpdatingRow) {
              autoPopObj = cells[IssueAndServiceSharedValues.teamMembers];
            } else {
              autoPopObj = [dropdownTable['lead'].find((member) => member._id === currentUser.id)];
            }
            break;
          case 'file':
            break;
          default:
            if (IssueKeys[column]) {
              autoPopObj = importObject[keys[column]];
            }
            break;
        }
        if (autoPopObj) {
          autoPopulateState[column] = autoPopObj;
        }
      });

      return autoPopulateState;
    },
    [eventHandler.type, linkedType, issueId, columns, isUpdatingRow, cells, dropdownTable, currentUser.id]
  );

  if (linkedType) {
    initialState = { ...initialState, ...autoPopulate(cells) };
  }

  if (forAutoTracker) {
    initialState = { ...initialState, ...cells };
  }

  const [fields, setFields] = useState(initialState);
  const [errorCols, setErrorCols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkedServiceId, setLinkedServiceId] = useState(null);

  const linkedServiceQuery = useQuery({
    queryKey: ['serviceById', linkedServiceId],
    queryFn: () => getServiceById(linkedServiceId),
    enabled: Boolean(linkedServiceId),
  });
  const sendNotificationMutation = useMutation({
    mutationFn: ({ destUserIds, postId, postTitle, type }) => sendNotification(destUserIds, postId, postTitle, type),
    onError: (error) => {
      console.error('Failed to send notification', error?.message ?? error);
    },
  });

  useEffect(() => {
    const service = linkedServiceQuery.data?.data;
    if (!service) return;
    const processedService = processFromDBForAutoComplete(service, ServiceKeys, dropdownTable);
    setFields((prev) => ({
      ...prev,
      ...autoPopulate(processedService),
      linkedService: prev.linkedService,
    }));
    setLoading(false);
    setLinkedServiceId(null);
  }, [linkedServiceQuery.data, dropdownTable, autoPopulate]);

  useEffect(() => {
    if (!linkedServiceQuery.isError) return;
    console.error('Failed to load linked service', linkedServiceQuery.error);
    setLoading(false);
    setLinkedServiceId(null);
  }, [linkedServiceQuery.isError, linkedServiceQuery.error]);

  const requiredInputs =
    eventHandler.type === TrackerDataType.MEDIA_INTERACTION
      ? [
          queryAdderMediaFields.topic,
          queryAdderMediaFields.entryDate,
          queryAdderMediaFields.type,
          queryAdderMediaFields.units,
          queryAdderMediaFields.journalist,
          queryAdderMediaFields.status,
          queryAdderMediaFields.lead,
          queryAdderMediaFields.expert,
        ]
      : eventHandler.type === TrackerDataType.ENGAGEMENT
      ? [
          queryAdderEngagementFields.topic,
          queryAdderEngagementFields.entryDate,
          queryAdderEngagementFields.type,
          queryAdderEngagementFields.units,
          queryAdderEngagementFields.communityMember,
          queryAdderEngagementFields.status,
          queryAdderEngagementFields.lead,
          queryAdderEngagementFields.expert,
        ]
      : [
          queryAdderServiceKeys.service,
          queryAdderServiceKeys.entryDate,
          // queryAdderServiceKeys.diversity,
          queryAdderServiceKeys.type,
          queryAdderServiceKeys.units,
          queryAdderServiceKeys.teamMember,
          queryAdderServiceKeys.status,
          queryAdderServiceKeys.unitLead,
        ];

  const validateInput = () => {
    const invalidCols = [];
    requiredInputs.forEach((input) => {
      if (fields[input] === null || fields[input] === undefined || fields[input] === '') {
        invalidCols.push(input);
        return;
      }
      if (Array.isArray(fields[input]) && fields[input].length < 1) {
        invalidCols.push(input);
      }
    });
    setErrorCols(invalidCols);
    return invalidCols.length < 1;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0] && isDragActive) {
      let newFiles = [...files] || [];
      const transferredFiles = e.dataTransfer.files;

      for (const element of transferredFiles) {
        newFiles.push(element);
      }

      setFiles(newFiles);
    }
  };

  const setDateToStartOfDay = (date) => {
    return dayjs(date).tz(dayjs.tz.guess()).startOf('day').toDate();
  };

  const onSubmitForm = async (e) => {
    e.preventDefault();
    fields['year'] = dayjs.utc(new Date(fields.date)).format('YYYY');
    fields['month'] = dayjs.utc(new Date(fields.date)).format('MMM');

    delete fields.outlet;
    try {
      setIsUpdatingItem(true);
      if (validateInput()) {
        const createNewItemFields = {
          ...fields,
          entryDate: setDateToStartOfDay(fields.entryDate),
        };
        if (fields.dueDate) {
          createNewItemFields.dueDate = setDateToStartOfDay(fields.dueDate);
        }

        eventHandler.createNewItem(createNewItemFields, files, ({ postId }) => {
          setOpenForm(false);

          if (
            eventHandler.type === TrackerDataType.MEDIA_INTERACTION ||
            eventHandler.type === TrackerDataType.ENGAGEMENT
          ) {
            sendNotificationMutation.mutate({
              destUserIds:
                fields.lead
                  ?.map((teamMember) => teamMember._id)
                  .filter((teamMemberId) => teamMemberId !== currentUser.id) ?? [],
              postId,
              postTitle: fields.topic,
              type: eventHandler.type,
            });
          } else {
            sendNotificationMutation.mutate({
              destUserIds:
                fields.teamMember
                  ?.map((teamMember) => teamMember._id)
                  .filter((teamMemberId) => teamMemberId !== currentUser.id) ?? [],
              postId,
              postTitle: fields.service,
              type: eventHandler.type,
            });
          }
          if (forAutoTracker) {
            const attachmentsToSubmit = files.filter((f) => f.type === 'autotrackerAttachment');
            if (eventHandler.type === TrackerDataType.SERVICE) {
              emailScrapeCallback(fields, email ?? undefined, undefined, postId, attachmentsToSubmit);
            } else {
              emailScrapeCallback(fields, email ?? undefined, undefined);
            }
          }
        });
      } else {
        setIsUpdatingItem(false);
      }
    } catch (e) {
      setIsUpdatingItem(false);
      console.log(e.message);
    }
  };

  const onSubmitUpdateForm = async (e) => {
    e.preventDefault();
    try {
      setIsUpdatingItem(true);
      if (validateInput()) {
        const uploadingFiles = files.filter((f) => f.type !== 'url').filter((file) => !Object.hasOwn(file, '_id'));
        const uploadingUrls = files
          .filter((f) => f.type === 'url')
          .filter((url) => !Object.hasOwn(url, '_id'))
          .map((url) => ({
            type: 'url',
            name: url.name,
            description: url.description,
            path: url.url ? url.url : url.path,
          }));
        const removingFiles = deletingFiles.filter((f) => f._id).filter((f) => f.type !== 'url');
        const removingUrls = deletingFiles.filter((f) => f._id).filter((f) => f.type === 'url');

        const updateItemFields = {
          ...fields,
          entryDate: setDateToStartOfDay(fields.entryDate),
        };
        if (fields.dueDate) {
          updateItemFields.dueDate = setDateToStartOfDay(fields.dueDate);
        }

        await eventHandler.updateItemWithFile(
          { id: issueId, fields: updateItemFields },
          uploadingFiles,
          uploadingUrls,
          removingFiles,
          removingUrls
        );

        const toIdArray = (members) => {
          if (!members) {
            return [];
          }
          const normalizedMembers = Array.isArray(members) ? members : [members];
          return normalizedMembers
            .map((member) => {
              if (!member) {
                return null;
              }
              if (typeof member === 'string') {
                return member;
              }
              return member._id ?? member.id ?? null;
            })
            .filter(Boolean);
        };

        const previousMemberIds = toIdArray(cells[IssueAndServiceSharedValues.teamMembers]);
        const isMediaInteractionOrEngagement =
          eventHandler.type === TrackerDataType.MEDIA_INTERACTION || eventHandler.type === TrackerDataType.ENGAGEMENT;
        const updatedMemberIds = toIdArray(isMediaInteractionOrEngagement ? fields.lead : fields.teamMember);
        const membersToNotify = updatedMemberIds.filter(
          (memberId) => memberId !== currentUser.id && !previousMemberIds.includes(memberId)
        );

        if (membersToNotify.length > 0) {
          await sendNotificationMutation.mutateAsync({
            destUserIds: membersToNotify,
            postId: issueId,
            postTitle: isMediaInteractionOrEngagement ? fields.topic : fields.service,
            type: eventHandler.type,
          });
        }
        setOpenForm(false);
      } else {
        setIsUpdatingItem(false);
      }
    } catch (err) {
      setIsUpdatingItem(false);
      console.error(err);
    }
  };

  const normalizeValues = (input) => {
    if (Array.isArray(input)) {
      return input;
    }
    if (input) {
      return [input];
    }
    return [];
  };

  const handleChange = (val, col) => {
    let newField;
    let autoOrganizationsToAdd = null;
    let organizationIdsAfterChange = null;

    switch (col) {
      case 'journalist':
        newField = val;
        break;
      case 'communityMember': {
        const communityMembers = normalizeValues(val);
        newField = communityMembers;

        const organizationLookup = dropdownTable['organization'] || [];
        autoOrganizationsToAdd = communityMembers
          .map((communityMember) => {
            const organizationRef = communityMember?.Organization;
            if (!organizationRef) {
              return null;
            }

            if (typeof organizationRef === 'string') {
              return organizationLookup.find((organization) => organization._id === organizationRef) ?? null;
            }
            if (organizationRef._id) {
              return (
                organizationLookup.find((organization) => organization._id === organizationRef._id) ?? organizationRef
              );
            }
            if (organizationRef.id) {
              return organizationLookup.find((organization) => organization._id === organizationRef.id) ?? null;
            }
            return null;
          })
          .filter(Boolean);
        break;
      }
      case 'expert':
        newField = val;
        break;
      case 'organization': {
        const organizations = normalizeValues(val);
        newField = organizations;
        organizationIdsAfterChange = organizations
          .map((organization) => organization?._id ?? organization)
          .filter(Boolean);
        break;
      }
      case 'outlet':
        newField = val?._id;
        break;
      case 'teamMember':
        newField = val;
        break;
      case 'topic':
      case 'response':
      case 'lead':
      case 'type':
      case 'date':
      case 'entryDate':
      case 'publishDate':
      case 'dueDate':
      case 'service':
      case 'unitLead':
      case 'notes':
      case 'background':
      case 'keyMessaging':
      case 'actions':
      case 'tone':
      case 'linkedInteraction':
      case 'linkedEngagement':
      case 'complexity':
      case 'status':
        newField = val;
        break;
      case 'linkedService':
        if (val.length > 0) {
          //since linkedServices can be multiple services, choose the one which was just added
          const linkedServ = dropdownTable['service'].find((service) => service._id === val[val.length - 1]._id);
          setLoading(true);
          if (linkedServ?._id) {
            setLinkedServiceId(linkedServ._id);
          } else {
            setLoading(false);
          }
        }
        newField = val;
        break;
      case 'campaign':
        newField = val.map((campaign) => {
          return campaign;
        });
        break;
      case 'units':
        newField = val.map((unit) => {
          return unit;
        });
        break;
      case 'diversity':
        newField = val?._id;
        break;
      case 'file': {
        if (val.deleted) {
          setDeletingFiles([...deletingFiles, val.deleted]);
          setFiles(val.files);
        } else {
          setFiles(val);
        }
        break;
      }
      case 'dept':
        newField = val.map((dept) => {
          return dept?.name;
        });
        break;
      default:
        newField = val?.name;
        break;
    }
    setFields((fields) => ({ ...fields, [col]: newField }));

    if (autoOrganizationsToAdd) {
      setFields((prev) => {
        const existingOrganizations = Array.isArray(prev.organization) ? [...prev.organization] : [];
        for (const organization of autoOrganizationsToAdd) {
          if (
            organization &&
            !existingOrganizations.some((existingOrganization) => existingOrganization._id === organization._id)
          ) {
            existingOrganizations.push(organization);
          }
        }
        return { ...prev, organization: existingOrganizations };
      });
    }

    if (organizationIdsAfterChange) {
      setFields((prev) => {
        if (!Array.isArray(prev.communityMember)) {
          return prev;
        }

        const filteredCommunityMembers = prev.communityMember.filter((member) => {
          const organizationRef = member?.Organization;
          if (!organizationRef) return true;

          let organizationId = null;
          if (typeof organizationRef === 'string') {
            organizationId = organizationRef;
          } else if (organizationRef?._id) {
            organizationId = organizationRef._id;
          } else if (organizationRef?.id) {
            organizationId = organizationRef.id;
          }

          if (!organizationId) {
            return true;
          }

          if (organizationIdsAfterChange.length === 0) {
            return false;
          }

          return organizationIdsAfterChange.includes(organizationId);
        });

        return { ...prev, communityMember: filteredCommunityMembers };
      });
    }

    col === 'outlet' && setFields((fields) => ({ ...fields, journalist: null }));
    col === 'journalist' &&
      setFields((fields) => ({
        ...fields,
        outlet: findOrganizationFromContact(newField, dropdownTable['outlet'], 'Org'),
      }));
    if (col === 'unitLead' || col === 'expert') {
      const currentSelectedExperts = Array.isArray(newField) ? newField : [];

      const unitsFromSelectedExperts = currentSelectedExperts
        .flatMap((expert) => {
          if (!dropdownTable || !dropdownTable['units'] || !Array.isArray(dropdownTable['units'])) {
            return null;
          }

          if (expert && expert.units && expert.units.length > 0) {
            const expertUnitIds = new Set(expert.units);

            return dropdownTable['units'].filter((unit) => expertUnitIds.has(unit._id)).map((unitObj) => unitObj.name);
          }

          // edge case for experts that do not have units field but have unit field
          if (!expert || !expert.unit) {
            return null;
          }
          const unitObj = dropdownTable['units'].find((u) => u._id === expert.unit);
          return unitObj ? unitObj.name : null;
        })
        .filter(Boolean);

      setFields((prevFields) => {
        const existingUnits = prevFields.units && Array.isArray(prevFields.units) ? prevFields.units : [];
        const combinedUnits = [...new Set([...existingUnits, ...unitsFromSelectedExperts])];
        return {
          ...prevFields,
          units: combinedUnits,
        };
      });
    }
  };

  const getSubmitButtonColor = () => {
    switch (eventHandler.type) {
      case TrackerDataType.MEDIA_INTERACTION:
        return 'mediaInteraction';
      case TrackerDataType.ENGAGEMENT:
        return 'primaryLighter';
      default:
        return 'primary';
    }
  };

  let submitFormButton = (
    <LoadingButton
      loading={isUpdatingItem}
      width={200}
      color={getSubmitButtonColor()}
      variant="contained"
      onClick={isUpdatingRow ? onSubmitUpdateForm : onSubmitForm}
      type="submit"
      className={classes.adderButton}
      data-cy={isUpdatingRow ? 'update-button-QueryAdder' : 'submit-button-QueryAdder'}
      startIcon={isUpdatingRow ? <SaveIcon /> : <AddIcon />}
    >
      {isUpdatingRow ? 'Save' : 'Add'}
    </LoadingButton>
  );

  if (loading) {
    return <LinearProgress />;
  }
  return (
    <ErrorBoundary>
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Typography align="center" className={classes.title} data-cy="queryAdder-title">
            {title}
          </Typography>
        </Box>
        <Box>
          {submitFormButton}
          <Button
            width={200}
            color="primary"
            variant="outlined"
            onClick={() => {
              setOpenForm(false);
            }}
            className={classes.cancelButton}
            data-cy="cancel-button-QueryAdder"
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
        </Box>
      </Stack>
      <Divider sx={{ borderColor: 'black', margin: '10px 0' }} />
      <form className={classes.container} onSubmit={onSubmitForm}>
        {columns.map((col) => {
          return !textInput.includes(col) ? (
            <QueryAdderCell
              key={col}
              classes={classes}
              col={col}
              dropdownTable={dropdownTable}
              fields={fields}
              callback={handleChange}
              defaultMonth={props.currMonth}
              required={requiredInputs.includes(col)}
              error={errorCols.includes(col)}
              eventHandler={eventHandler}
              files={files}
              handleDrag={handleDrag}
              handleDrop={handleDrop}
              isDragActive={isDragActive}
              isUpdating={isUpdatingRow}
            />
          ) : (
            <QueryAdderCell
              key={col}
              classes={classes}
              col={col}
              dropdownTable={dropdownTable}
              fields={fields}
              callback={handleChange}
              defaultMonth={props.currMonth}
              className={classes.textInput}
              required={requiredInputs.includes(col)}
              error={errorCols.includes(col)}
              eventHandler={eventHandler}
              isUpdating={isUpdatingRow}
            />
          );
        })}
      </form>
      {onDelete && (
        <>
          <Divider sx={{ borderColor: 'black', margin: '10px 0' }} />
          <Button
            width={200}
            color="error"
            variant="contained"
            startIcon={<DeleteForeverIcon />}
            data-cy="delete-button-QueryAdder"
            onClick={() => setOpenVerificationModal(true)}
          >
            Delete
          </Button>
          <VerificationModal
            openVerificationModal={openVerificationModal}
            setOpenVerificationModal={setOpenVerificationModal}
            modalTitle={'Confirm Deletion'}
            modalDescription={`Are you sure you want to delete this ${
              eventHandler.type === TrackerDataType.MEDIA_INTERACTION
                ? 'Media Interaction'
                : eventHandler.type === TrackerDataType.ENGAGEMENT
                ? 'Engagement'
                : 'Service'
            }?`}
            handleYesClick={() => {
              onDelete();
              setOpenVerificationModal(false);
            }}
          />
        </>
      )}
    </ErrorBoundary>
  );
}
